import apiClient from './client';

import type {
  CanvasAssetType,
  CanvasContextType,
  CanvasItem,
  CanvasItemsPatchRequest,
  CanvasItemsResponse,
  CanvasLayoutWriteResult,
  CanvasPersistentItemType,
} from '@/types/canvas';

const CONTEXT_TYPE_API_VALUE: Record<CanvasContextType, number> = {
  asset: 1,
  storyboard: 2,
};

const ASSET_TYPE_API_VALUE: Record<CanvasAssetType, number> = {
  character: 1,
  location: 2,
  prop: 3,
};

const ITEM_TYPE_API_VALUE: Record<CanvasPersistentItemType, number> = {
  resource_pin: 15,
  storyboard_temporary_video: 21,
};

const ITEM_TYPE_API_NAME: Record<number, CanvasPersistentItemType> = {
  15: 'resource_pin',
  21: 'storyboard_temporary_video',
};

function readObjectValue(record: Record<string, unknown>, camelKey: string, snakeKey: string) {
  return record[camelKey] ?? record[snakeKey];
}

export function normalizeCanvasItem(value: unknown): CanvasItem {
  const item = value as Record<string, unknown>;
  const rawItemType = readObjectValue(item, 'itemType', 'item_type');
  const itemType =
    typeof rawItemType === 'number'
      ? ITEM_TYPE_API_NAME[rawItemType]
      : (rawItemType as CanvasPersistentItemType);
  const metaData = readObjectValue(item, 'metaData', 'meta_data');

  return {
    canvasItemId: readObjectValue(item, 'canvasItemId', 'canvas_item_id') as string,
    itemType,
    itemId: readObjectValue(item, 'itemId', 'item_id') as string,
    sourceResourceType: readObjectValue(
      item,
      'sourceResourceType',
      'source_resource_type'
    ) as CanvasItem['sourceResourceType'],
    sourceResourceId: readObjectValue(item, 'sourceResourceId', 'source_resource_id') as
      | string
      | null
      | undefined,
    metaData:
      metaData && typeof metaData === 'object' && !Array.isArray(metaData)
        ? (metaData as Record<string, unknown>)
        : {},
    x: 0,
    y: 0,
    zIndex: 0,
    createdAt: readObjectValue(item, 'createdAt', 'created_at') as string,
    updatedAt: readObjectValue(item, 'updatedAt', 'updated_at') as string,
  };
}

function serializeRequest(payload: CanvasItemsPatchRequest) {
  return {
    contextType: CONTEXT_TYPE_API_VALUE[payload.contextType],
    assetType:
      payload.assetType == null ? payload.assetType : ASSET_TYPE_API_VALUE[payload.assetType],
    assetId: payload.assetId,
    storyboardId: payload.storyboardId,
    items: payload.items.flatMap((item) => {
      const itemType = ITEM_TYPE_API_VALUE[item.itemType as CanvasPersistentItemType];
      if (itemType === undefined) return [];
      return [
        {
          itemType,
          itemId: item.itemId,
          sourceResourceType: item.sourceResourceType,
          sourceResourceId: item.sourceResourceId,
          metaData: item.metaData ?? {},
        },
      ];
    }),
  };
}

export const canvasItemsApi = {
  async list(params: {
    projectId: string;
    contextType: CanvasContextType;
    assetType?: CanvasAssetType | null;
    assetId?: string | null;
    storyboardId?: string | null;
  }): Promise<CanvasItemsResponse> {
    const response = (await apiClient.get(`/projects/${params.projectId}/canvas/items`, {
      params: {
        contextType: params.contextType,
        assetType: params.assetType ?? undefined,
        assetId: params.assetId ?? undefined,
        storyboardId: params.storyboardId ?? undefined,
      },
    })) as unknown as CanvasItemsResponse;
    return {
      ...response,
      items: response.items.map(normalizeCanvasItem),
    };
  },

  patch(projectId: string, payload: CanvasItemsPatchRequest): Promise<CanvasLayoutWriteResult> {
    return apiClient.patch(`/projects/${projectId}/canvas/items`, serializeRequest(payload));
  },
};
